// filterView/variants/chipsView.js — variant A "Suchleiste" of the documents.php filter views (plan
// documents_admin-search, phase filter-varianten): type, Enter, done; everything else when needed.
//
//   search bar   the search bar field (quick: 'search', "Titel und Schlagworte") · contains, with a "Suchen" submit
//   chips        one select per quick field (visible, type, category, medium): "alle" + choices, mode is
//   active tags  "Field mode „value“" per complete condition, × removes it
//   more         "Weitere Filter": the combine sentence and the condition rows of the builder
//
// Split of the shared list (User 2026-10-13): the search bar shows the first search-bar · contains condition, a chip the
// first is-condition of its field (with a choice it has), the rows everything else. A condition once shown in the rows
// stays there while it exists — editing a row into "Bildtyp ist …" does not make it jump into the chip. "Weitere Filter"
// opens by itself when it gets conditions and is always open while the list is "mindestens eine", so that is never
// hidden.
//
// createChipsView({ schema, store }) → { element, destroy }

import { createDisposers } from '../../core/disposers.js';
import { addCondition, updateCondition, removeCondition, validate } from '../conditionSet.js';
import { createValueInput } from '../parts/valueInput.js';
import { createConditionRows } from '../parts/conditionRows.js';
import { createCombineSentence } from '../parts/combineToggle.js';
import { defaultAddField } from './builderView.js';

let instances = 0;

export function createChipsView({ schema, store }) {
    const disposers = createDisposers();
    const texts = schema.texts;
    const searchField = schema.fields.find((f) => f.quick === 'search') ?? null;
    const quickFields = schema.fields.filter((f) => f.quick === true);
    const inRows = new Set();   // condition ids shown in the rows once — they stay there
    const listen = (target, type, handler) => {
        target.addEventListener(type, handler);
        disposers.add(() => target.removeEventListener(type, handler));
    };

    // the condition a control shows: add when a value comes, remove when it goes, else change the value
    const write = (condition, field, mode, value) => {
        if (condition && value === '') {
            store.set((set) => removeCondition(set, condition.id));
        } else if (condition) {
            store.set((set) => updateCondition(set, schema, condition.id, { value }));
        } else if (value !== '') {
            store.set((set) => addCondition(set, schema, { field: field.key, mode, value }));
        }
    };

    const element = document.createElement('div');
    element.className = 'topo2-filterview topo2-filterview--chips';

    // ── search bar
    let searchCondition = null;
    let searchInput = null;
    if (searchField) {
        const bar = document.createElement('div');
        bar.className = 'topo2-filterview-searchbar';
        searchInput = document.createElement('input');
        searchInput.type = 'search';
        searchInput.className = 'topo2-field-textinput topo2-field--bordered topo2-filterview-search';
        searchInput.placeholder = texts.FILTER_VIEW_SEARCH_PLACEHOLDER;
        searchInput.setAttribute('aria-label', searchField.label);
        listen(searchInput, 'input', () => write(searchCondition, searchField, 'contains', searchInput.value));
        // submits the filter form like OK (Enter in the field does the same)
        const submit = document.createElement('button');
        submit.type = 'submit';
        submit.className = 'topo2-btn topo2-btn--solid topo2-btn--bar topo2-btn--small topo2-filterview-search-btn';
        submit.textContent = texts.FILTER_VIEW_SEARCH;
        bar.append(searchInput, submit);
        element.append(bar);
    }

    // ── chips + "Weitere Filter" toggle
    const chipsEl = document.createElement('div');
    chipsEl.className = 'topo2-filterview-chips';
    const chips = quickFields.map((field) => {
        const chip = { field, condition: null };
        chip.element = document.createElement('label');
        chip.element.className = 'topo2-filterview-chip';
        const name = document.createElement('span');
        name.textContent = `${field.label}:`;
        chip.input = createValueInput({
            field, needsValue: true, emptyLabel: texts.SEARCH_NOT, label: field.label,
            onInput: (value) => write(chip.condition, field, 'is', value),
        });
        disposers.add(() => chip.input.destroy());
        chip.element.append(name, chip.input.element);
        chipsEl.append(chip.element);
        return chip;
    });

    const moreId = `topo2-fv-chips${++instances}-more`;
    const moreToggle = document.createElement('button');
    moreToggle.type = 'button';
    moreToggle.className = 'topo2-btn topo2-btn--outline topo2-btn--bar topo2-btn--extrasmall topo2-filterview-more-toggle';
    moreToggle.textContent = texts.FILTER_VIEW_MORE_FILTERS;
    moreToggle.setAttribute('aria-controls', moreId);
    chipsEl.append(moreToggle);
    element.append(chipsEl);

    // ── active tags (× through one delegated listener; the tags are rebuilt on every change)
    const activeEl = document.createElement('div');
    activeEl.className = 'topo2-filterview-active';
    listen(activeEl, 'click', (ev) => {
        const button = ev.target.closest('[data-condition-id]');
        if (button) {
            store.set((set) => removeCondition(set, button.dataset.conditionId));
        }
    });
    element.append(activeEl);

    // ── "Weitere Filter"
    const more = document.createElement('div');
    more.className = 'topo2-filterview-more';
    more.id = moreId;
    const lead = createCombineSentence({ store, texts });
    disposers.add(() => lead.destroy());
    const rows = createConditionRows({ schema, store, addField: defaultAddField(schema) });
    disposers.add(() => rows.destroy());
    more.append(lead.element, rows.element);
    element.append(more);

    let moreOpen = null;   // the user's / automatic open state; null = not decided yet
    let restCount = 0;
    listen(moreToggle, 'click', () => {
        moreOpen = !moreOpen;
        render(store.get());
    });

    const describe = (condition) => {
        const field = schema.fields.find((f) => f.key === condition.field);
        const mode = field.modes.find((m) => m.key === condition.mode);
        if (!mode.value) {
            return `${field.label} ${mode.label}`;
        }
        const shown = field.options?.find((o) => o.value === condition.value)?.label ?? condition.value;
        return `${field.label} ${mode.label} „${shown}“`;
    };

    function render(set) {
        const problems = new Map(validate(set, schema).map((p) => [p.id, p.reason]));

        // split: search bar, chips, rows
        let bar = null;
        const chipConditions = new Map();
        const rest = [];
        for (const condition of set.conditions) {
            const quick = chips.find((c) => c.field.key === condition.field);
            if (inRows.has(condition.id)) {
                rest.push(condition);
            } else if (!bar && searchField && condition.field === searchField.key && condition.mode === 'contains') {
                bar = condition;
            } else if (quick && condition.mode === 'is' && !chipConditions.has(condition.field)
                && quick.field.options?.some((o) => o.value === condition.value)) {
                chipConditions.set(condition.field, condition);
            } else {
                rest.push(condition);
            }
        }
        const restIds = new Set(rest.map((c) => c.id));
        for (const id of inRows) {
            if (!restIds.has(id)) {
                inRows.delete(id);
            }
        }
        for (const id of restIds) {
            inRows.add(id);
        }

        searchCondition = bar;
        if (searchInput && searchInput.value !== (bar?.value ?? '')) {
            searchInput.value = bar?.value ?? '';
        }
        for (const chip of chips) {
            chip.condition = chipConditions.get(chip.field.key) ?? null;
            chip.input.setValue(chip.condition?.value ?? '');
            chip.element.classList.toggle('is-set', chip.condition !== null);
        }

        rows.render(rest, problems);
        lead.update(set.combine);

        // "Weitere Filter": opens when it gets conditions, always open for "mindestens eine"
        if (moreOpen === null || (rest.length > restCount)) {
            moreOpen = moreOpen || rest.length > 0;
        }
        restCount = rest.length;
        const forced = set.combine === 'or';
        const open = forced || moreOpen;
        more.hidden = !open;
        moreToggle.setAttribute('aria-expanded', String(open));
        moreToggle.disabled = forced;

        // active tags: complete conditions only (an unfinished row is not active)
        const tags = set.conditions.filter((c) => problems.get(c.id) !== 'value').map((condition) => {
            const label = describe(condition);
            const tag = document.createElement('span');
            tag.className = 'topo2-filterview-tag';
            tag.append(label);
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'topo2-btn topo2-btn--noborder topo2-filterview-tag-remove';
            remove.dataset.conditionId = condition.id;
            remove.setAttribute('aria-label', texts.FILTER_VIEW_REMOVE_TAG.replace('{label}', label));
            remove.textContent = '×';
            tag.append(remove);
            return tag;
        });
        const caption = document.createElement('span');
        caption.className = 'topo2-filterview-active-caption';
        caption.textContent = tags.length ? texts.FILTER_VIEW_ACTIVE_TAGS : texts.FILTER_VIEW_NONE_ACTIVE;
        activeEl.replaceChildren(caption, ...tags);
    }

    disposers.add(store.subscribe(render));
    render(store.get());

    return {
        element,
        destroy() {
            disposers.dispose();
        },
    };
}
